// WO-2026-08-21 Track E (design repo docs/designs/2026-08-21-find-reviewers/): the
// "Claim a Prophy Suggested Reviewer" modal — four steps off one `step` state.
//
//   review  — editable email + affiliation ("please make sure this is correct"); read-only
//             H-index + ORCID ("pulled from Prophy — not editable").
//   unable  — why can't you evaluate? → releases the candidate out of the pool.
//   reject  — why are you rejecting? → Student reject (out of the pool, badged in Box 2).
//   confirm — attestation checkbox gates Continue to Part 2.
//
// The server claim (POST /referees/:id/claim, Q19 fields) happens ONLY at Continue to Part 2 —
// until then the candidate is still Unclaimed, so the unable/reject steps use the existing
// /referees/:id/resolve outcomes (reason required, audited). On claim the created proposal is
// handed to the caller, which opens the Part-2 evaluate form (FR-L3).
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProposalView, RefereeView } from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { Button, Field, Input, Modal, Textarea } from "../../components/ui.js";

type Step = "review" | "unable" | "reject" | "confirm";

export function ClaimModal({
  referee,
  preprintId,
  onClose,
  onClaimed,
}: {
  referee: RefereeView;
  preprintId: string;
  onClose: () => void;
  /** Called with the created (incomplete) proposal so the Part-2 form opens (FR-L3). */
  onClaimed: (p: ProposalView) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("review");
  const [email, setEmail] = useState("");
  const [affiliation, setAffiliation] = useState(referee.affiliations ?? "");
  const [reason, setReason] = useState("");
  const [attested, setAttested] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["referees"] });
    qc.invalidateQueries({ queryKey: ["proposals"] });
  };

  const claim = useMutation({
    mutationFn: () =>
      api.post<ProposalView>(`/referees/${referee.id}/claim`, {
        preprintId,
        reviewerEmail: email.trim(),
        reviewerAffiliation: affiliation.trim(),
      }),
    onSuccess: (created) => {
      invalidate();
      onClaimed(created);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError && err.body && typeof err.body === "object" && "error" in err.body
          ? String((err.body as { error: string }).error)
          : "Could not claim this reviewer.",
      );
    },
  });
  const resolve = useMutation({
    mutationFn: (status: "Reject" | "Unable to Evaluate") =>
      api.post(`/referees/${referee.id}/resolve`, { preprintId, status, reason: reason.trim() }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: () => setError("Could not resolve this reviewer."),
  });

  const emailOk = /.+@.+\..+/.test(email.trim());
  const reviewOk = emailOk && affiliation.trim().length > 0;
  const busy = claim.isPending || resolve.isPending;

  const alert = error ? (
    <p className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" role="alert">
      {error}
    </p>
  ) : null;

  let body: ReactNode;
  let footer: ReactNode;
  if (step === "review") {
    body = (
      <>
        <p className="text-sm text-slate-600">
          Please see the below information provided by Prophy and complete your claim/proposal of
          this reviewer.
        </p>
        <Field label="Reviewer email" hint="Please make sure this email is correct." required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="reviewer@university.edu"
            required
          />
        </Field>
        <Field label="Reviewer affiliation" hint="Please make sure this affiliation is correct." required>
          <Input value={affiliation} onChange={(e) => setAffiliation(e.target.value)} required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="H-index" hint="Pulled from Prophy — not editable.">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
              {referee.hIndex ?? "—"}
            </div>
          </Field>
          <Field label="ORCID" hint="Pulled from Prophy — not editable.">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
              {referee.orcid ?? "—"}
            </div>
          </Field>
        </div>
        {alert}
      </>
    );
    footer = (
      <>
        <Button variant="secondary" onClick={() => { setReason(""); setStep("unable"); }} disabled={busy}>
          Unable to evaluate
        </Button>
        <Button variant="secondary" onClick={() => { setReason(""); setStep("reject"); }} disabled={busy}>
          Reject reviewer
        </Button>
        <span className="flex-1" />
        <Button onClick={() => setStep("confirm")} disabled={!reviewOk || busy}>
          Continue with proposal
        </Button>
      </>
    );
  } else if (step === "unable") {
    body = (
      <>
        <Field label="Why are you unable to evaluate this reviewer?">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="e.g. Their field is outside anything I can judge."
          />
        </Field>
        {alert}
      </>
    );
    footer = (
      <>
        <Button variant="secondary" onClick={() => setStep("review")} disabled={busy}>
          Back
        </Button>
        <span className="flex-1" />
        <Button onClick={() => resolve.mutate("Unable to Evaluate")} disabled={!reason.trim() || busy}>
          {resolve.isPending ? "Releasing…" : "Release claim"}
        </Button>
      </>
    );
  } else if (step === "reject") {
    body = (
      <>
        <Field label="Why are you rejecting this reviewer?">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="e.g. Retired from the field; no recent publications."
          />
        </Field>
        {alert}
      </>
    );
    footer = (
      <>
        <Button variant="secondary" onClick={() => setStep("review")} disabled={busy}>
          Back
        </Button>
        <span className="flex-1" />
        <Button variant="danger" onClick={() => resolve.mutate("Reject")} disabled={!reason.trim() || busy}>
          {resolve.isPending ? "Rejecting…" : "Student reject"}
        </Button>
      </>
    );
  } else {
    body = (
      <>
        <div className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2.5">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
            />
            <span>
              I have ensured that the reviewer email and affiliation are accurate and want to move
              forward with the proposal.
            </span>
          </label>
        </div>
        {!attested && (
          <p className="text-xs text-slate-400">Tick the attestation to continue to Part 2.</p>
        )}
        {alert}
      </>
    );
    footer = (
      <>
        <Button variant="secondary" onClick={() => setStep("review")} disabled={busy}>
          Back
        </Button>
        <span className="flex-1" />
        <Button onClick={() => claim.mutate()} disabled={!attested || busy}>
          {claim.isPending ? "Claiming…" : "Continue to Part 2"}
        </Button>
      </>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Claim a Prophy Suggested Reviewer — ${referee.name}`}
      className="max-w-xl"
      footer={footer}
    >
      <div className="space-y-3">{body}</div>
    </Modal>
  );
}
